import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiUser, FiMail, FiMessageSquare, FiArrowRight } from "react-icons/fi";
import { FaPhoneVolume, FaEnvelope, FaLocationDot, FaClock, FaRegBuilding } from "react-icons/fa6";
import { BTN_SIZE, Container } from "../home/ui";
import { contactInfo } from "./contactInfo";

// Same mailer + thank-you redirect as the home page contact form and the old contact page.
const MAILER_URL = "https://mailer.datametricks.com/send-email.php";

// Title of the two big cards (between the H2 and H3 sizes, as in the design).
const CARD_TITLE = "text-[22px] xl:text-[clamp(20px,1.45vw,26px)]";

const inputCls =
  "w-full rounded-[12px] border-[1px] border-solid border-[#DCE3F0] bg-white text-[14.5px] text-[#0B1A4A] outline-none transition placeholder:text-slate-400 focus:border-[#1E6BFF] focus:ring-4 focus:ring-blue-100";

const info = [
  {
    icon: FaPhoneVolume,
    title: "Call Us",
    value: contactInfo.phone,
    href: contactInfo.phoneHref,
    note: contactInfo.hours,
    tone: "bg-[#E3EDFF] text-[#2563EB]",
  },
  {
    icon: FaEnvelope,
    title: "Email Us",
    value: contactInfo.email,
    href: `mailto:${contactInfo.email}`,
    note: "We reply within 24 hours",
    tone: "bg-[#FFE4E6] text-[#EF4444]",
  },
  {
    icon: FaLocationDot,
    title: "Our Office",
    value: contactInfo.addressLine1,
    note: contactInfo.addressLine2,
    tone: "bg-[#DFF7EC] text-[#10B981]",
  },
  {
    icon: FaClock,
    title: "Working Hours",
    value: contactInfo.hours,
    note: contactInfo.closed,
    tone: "bg-[#EEE6FF] text-[#7C3AED]",
  },
];

const Field = ({ icon: Icon, error, children }) => (
  <div>
    <div className="relative">
      <Icon className="pointer-events-none absolute left-[16px] top-[17px] text-[17px] text-slate-500" />
      {children}
    </div>
    {error && <p className="mt-1 text-[12px] text-rose-500">{error}</p>}
  </div>
);

const ContactForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.subject.trim()) e.subject = "Please enter a subject.";
    if (form.message.trim().length < 10) e.message = "Please enter at least 10 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setStatus("");
    try {
      const res = await fetch(MAILER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: "N/A",
          message: `Subject: ${form.subject}\n\n${form.message}`,
        }),
      });
      const result = await res.json();
      if (result.status === "success") {
        navigate("/Successemailpage");
        return;
      }
      setStatus(result.message || "Something went wrong. Please try again.");
    } catch (err) {
      setStatus("Error submitting form. Please try again.");
    }
    setLoading(false);
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="min-w-0 rounded-[20px] bg-white p-[22px] shadow-[0_18px_50px_-24px_rgba(40,60,140,0.3)] max-sm:p-[18px] sm:p-[28px]"
    >
      <h2 className={`font-extrabold tracking-tight text-[#0B1A4A] ${CARD_TITLE}`}>
        Send Us a Message
      </h2>
      <p className="mt-[6px] text-[14.5px] text-slate-500">Fill out the form below and we&apos;ll get back to you shortly.</p>

      <div className="mt-[20px] grid grid-cols-1 gap-[16px] sm:grid-cols-2">
        <Field icon={FiUser} error={errors.name}>
          <input
            name="name"
            aria-label="Your Name"
            value={form.name}
            onChange={onChange}
            placeholder="Your Name"
            className={`${inputCls} h-[52px] pl-[46px] pr-[16px]`}
          />
        </Field>
        <Field icon={FiMail} error={errors.email}>
          <input
            name="email"
            type="email"
            aria-label="Your Email"
            value={form.email}
            onChange={onChange}
            placeholder="Your Email"
            className={`${inputCls} h-[52px] pl-[46px] pr-[16px]`}
          />
        </Field>
        <div className="sm:col-span-2">
          <Field icon={FaRegBuilding} error={errors.subject}>
            <input
              name="subject"
              aria-label="Subject"
              value={form.subject}
              onChange={onChange}
              placeholder="Subject"
              className={`${inputCls} h-[52px] pl-[46px] pr-[16px]`}
            />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field icon={FiMessageSquare} error={errors.message}>
            <textarea
              name="message"
              aria-label="Your Message"
              rows={5}
              value={form.message}
              onChange={onChange}
              placeholder="Your Message"
              className={`${inputCls} block min-h-[140px] resize-y py-[14px] pl-[46px] pr-[16px]`}
            />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className={`group mt-[20px] flex w-full items-center justify-center rounded-[12px] border-0 bg-[#1E5BFF] font-medium text-white shadow-[0_14px_30px_-14px_rgba(30,91,255,0.8)] transition hover:brightness-110 disabled:opacity-60 ${BTN_SIZE}`}
      >
        {loading ? "Sending..." : "Send Message"}
        <FiArrowRight className="text-[16px] transition group-hover:translate-x-1" />
      </button>
      {status && <p className="mt-[12px] text-center text-[13px] text-rose-500">{status}</p>}
    </form>
  );
};

const ContactFormInfo = () => (
  <section className="bg-[#F7F9FE] pb-[40px] lg:pb-[56px]">
    <Container className="max-w-[1320px]">
      <div className="relative -mt-[16px] grid grid-cols-1 gap-[24px] lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <ContactForm />

        <div className="rounded-[20px] bg-white p-[22px] shadow-[0_18px_50px_-24px_rgba(40,60,140,0.3)] sm:p-[28px]">
          <h2 className={`font-extrabold tracking-tight text-[#0B1A4A] ${CARD_TITLE}`}>
            Contact Information
          </h2>
          <p className="mt-[6px] text-[14.5px] text-slate-500">Reach out to us through any of the following channels.</p>

          <ul className="mt-[22px] space-y-[22px]">
            {info.map((c) => (
              <li key={c.title} className="flex items-start gap-[18px]">
                <span className={`inline-flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full text-[22px] ${c.tone}`}>
                  <c.icon />
                </span>
                <div className="min-w-0 pt-[4px]">
                  <h3 className="text-[15.5px] font-bold text-[#0B1A4A]">{c.title}</h3>
                  {c.href ? (
                    <a href={c.href} className="mt-[4px] block break-words text-[15px] text-[#0B1A4A] hover:text-[#1E6BFF]">
                      {c.value}
                    </a>
                  ) : (
                    <p className="mt-[4px] text-[15px] text-[#0B1A4A]">{c.value}</p>
                  )}
                  <p className="mt-[2px] text-[13px] text-slate-500">{c.note}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Container>
  </section>
);

export default ContactFormInfo;
